import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { TrackedInvite, VendorRow } from '@/lib/contract';
import { formatDay, formatHours, formatPercent } from '@/lib/format';
import { PageHeader } from '@/ui/page-header';
import { Panel } from '@/ui/panel';
import { Button } from '@/ui/button';
import { Input } from '@/ui/input';
import { Badge } from '@/ui/badge';
import { TabRow } from '@/ui/tab-row';
import { StatusDot } from '@/ui/status-dot';
import { DataTable, type Column } from '@/ui/data-table';
import { ErrorState } from '@/ui/error-state';
import { EmptyState } from '@/ui/empty-state';
import { ModalityChips } from '@/domain/modality-chips';
import { ComplianceDots } from '@/domain/compliance-dots';
import { TierBadge } from '@/domain/tier-badge';

interface VendorsPayload {
  data: (VendorRow & { vendor_code: string | null; tracked_invites: TrackedInvite[] })[];
  tracked_invites: TrackedInvite[];
}

/**
 * An invitation admits someone into a specific organisation, so it has to name one. There is no
 * account-wide "quick link" the way the mockup implied: the link is a property of the vendor it was
 * created for, and is returned once, at creation, because only the hash is stored.
 */
interface CreatedVendor {
  vendor_code: string;
  name: string;
  invite_path: string;
}

const TABS = [
  { label: 'All Vendors', to: '/admin/vendors' },
  { label: 'Detail Drawer', to: '/admin/vendors/drawer' },
  { label: 'Detail Page', to: '/admin/vendors/page' }
];

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  trial_batch: 'Trial batch',
  registered: 'Registered',
  invited: 'Invited',
  suspended: 'Suspended'
};

function statusTone(status: string): 'pass' | 'warn' | 'muted' | 'primary' {
  if (status === 'active') return 'pass';
  if (status === 'trial_batch') return 'warn';
  if (status === 'registered') return 'primary';
  return 'muted';
}

/**
 * Vendor is the organisation, so the code belongs next to the name -- it is what the vendor sees in
 * their own topbar and what an operator says out loud.
 */
function vendorCell(r: VendorRow & { vendor_code: string | null }) {
  return (
    <div className="grid">
      <span className="text-[13px] text-foreground">{r.name}</span>
      {r.vendor_code ? <span className="font-mono text-[10px] text-dim">{r.vendor_code}</span> : null}
    </div>
  );
}

type Row = VendorRow & { vendor_code: string | null; tracked_invites: TrackedInvite[] };

const columns: Column<Row>[] = [
  { key: 'vendor', header: 'Vendor', className: 'w-[150px]', render: vendorCell },
  {
    key: 'status',
    header: 'Status',
    className: 'w-[110px]',
    render: r => (
      <StatusDot
        tone={statusTone(r.onboarding_status)}
        label={STATUS_LABEL[r.onboarding_status] ?? r.onboarding_status}
      />
    )
  },
  {
    key: 'hours',
    header: 'Hours',
    className: 'w-[80px]',
    render: r => <span className="font-mono text-[13px]">{formatHours(r.hours)}</span>
  },
  {
    key: 'modalities',
    header: 'Modalities',
    className: 'w-[180px]',
    render: r => <ModalityChips modalities={r.modalities} />
  },
  {
    key: 'qa',
    header: 'QA',
    className: 'w-[70px]',
    render: r =>
      r.qa_pass_rate === null ? (
        <span className="text-dim">—</span>
      ) : (
        <span className="font-mono text-[13px] text-pass">{formatPercent(r.qa_pass_rate)}</span>
      )
  },
  { key: 'tier', header: 'Tier', className: 'w-[60px]', render: r => <TierBadge tier={r.tier} /> },
  {
    key: 'compliance',
    header: 'Compliance',
    className: 'w-[210px]',
    render: r => <ComplianceDots compliance={r.compliance} />
  },
  {
    key: 'upload',
    header: 'Last upload',
    className: 'w-[100px]',
    render: r =>
      r.last_upload_at ? (
        <span className="font-mono text-[11px] text-muted">{formatDay(r.last_upload_at)}</span>
      ) : (
        <span className="text-dim">—</span>
      )
  },
  {
    key: 'owner',
    header: 'Owner',
    className: 'w-[80px]',
    render: r => <span className="text-[13px] text-muted">{r.owner_name ?? '—'}</span>
  }
];

const INVITE_TONE: Record<string, 'pass' | 'warn' | 'neutral' | 'danger'> = {
  registered: 'pass',
  opened: 'warn',
  sent: 'neutral',
  revoked: 'danger',
  expired: 'danger'
};

export function InternalVendors() {
  const [inviting, setInviting] = useState(false);
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'vendors'],
    queryFn: () => api<VendorsPayload>('/vendors')
  });

  const pending = data?.tracked_invites.filter(i => i.state !== 'registered').length ?? 0;

  return (
    <div className="grid gap-5">
      <PageHeader
        title="Vendors"
        subtitle={`${data?.data.length ?? 0} vendors · ${pending} pending invites · invite-only`}
        actions={<Button onClick={() => setInviting(v => !v)}>{inviting ? 'Cancel' : '+ Invite vendor'}</Button>}
      />

      <TabRow tabs={TABS} />

      <Panel bodyClassName="p-0">
        <div className="grid grid-cols-2 gap-6 p-5 max-lg:grid-cols-1">
          <CreateVendor open={inviting} onClose={() => setInviting(false)} />
          <TrackedInvites invites={data?.tracked_invites ?? []} />
        </div>
      </Panel>

      <Panel title="Vendor roster" subtitle="Lifecycle · capability · compliance" bodyClassName="px-0 pb-0">
        {isError ? (
          <ErrorState
            error={error}
            onRetry={() => void refetch()}
            title="Could not load the vendor roster"
            className="m-5"
          />
        ) : isPending ? (
          <EmptyState title="Loading vendors…" className="m-5" />
        ) : (
          <DataTable
            columns={columns}
            rows={data?.data ?? []}
            rowKey={row => row.id}
            className="rounded-none border-0 bg-transparent"
          />
        )}
      </Panel>
    </div>
  );
}

/**
 * Creating a vendor is creating a tenant: it is what gives the organisation its own isolated data,
 * so it cannot be a side effect of accepting a link. The invite token comes back exactly once,
 * because only its hash is stored.
 */
function CreateVendor({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [created, setCreated] = useState<CreatedVendor | null>(null);
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: () => api<CreatedVendor>('/vendors', { method: 'POST', body: JSON.stringify({ name, email }) }),
    onSuccess: vendor => {
      setCreated(vendor);
      setName('');
      setEmail('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'vendors'] });
    }
  });

  if (created) {
    const link = `${window.location.origin}${created.invite_path}`;
    return (
      <div className="grid content-start gap-3">
        <div className="grid gap-1">
          <h3 className="text-[15px] font-medium text-foreground">
            {created.name} · {created.vendor_code}
          </h3>
          <span className="font-mono text-[10px] tracking-[0.12em] text-dim uppercase">
            Single-use · expires in 7 days · shown once
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Input readOnly value={link} className="font-mono text-[12px]" />
          <Button
            variant="secondary"
            className="shrink-0"
            onClick={() => {
              void navigator.clipboard.writeText(link);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <p className="text-[12px] leading-relaxed text-muted">
          Only the hash is stored, so this link cannot be shown again. Issue a fresh invitation if it is lost.
        </p>
        <button
          type="button"
          onClick={() => {
            setCreated(null);
            onClose();
          }}
          className="cursor-pointer justify-self-start text-[12px] text-primary hover:underline"
        >
          Done
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="grid content-start gap-3">
        <div className="grid gap-1">
          <h3 className="text-[15px] font-medium text-foreground">Invitations</h3>
          <span className="font-mono text-[10px] tracking-[0.12em] text-dim uppercase">
            Invite-only · one organisation per invite
          </span>
        </div>
        <p className="text-[12px] leading-relaxed text-muted">
          Each invitation creates the vendor's own isolated workspace and admits one email address into it. Use{' '}
          <span className="text-foreground">Invite vendor</span> to issue one.
        </p>
      </div>
    );
  }

  return (
    <form
      className="grid content-start gap-3"
      onSubmit={event => {
        event.preventDefault();
        create.mutate();
      }}
    >
      <div className="grid gap-1">
        <h3 className="text-[15px] font-medium text-foreground">Invite a vendor</h3>
        <span className="font-mono text-[10px] tracking-[0.12em] text-dim uppercase">
          Creates the organisation and its first login
        </span>
      </div>
      <Input required placeholder="Organisation name" value={name} onChange={event => setName(event.target.value)} />
      <Input
        required
        type="email"
        placeholder="ops@vendor.com"
        value={email}
        onChange={event => setEmail(event.target.value)}
        className="font-mono text-[12px]"
      />
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={create.isPending || !name || !email}>
          {create.isPending ? 'Creating…' : 'Create and invite'}
        </Button>
        {create.isError ? (
          <span className="text-[12px] text-danger">
            {create.error instanceof Error ? create.error.message : 'Could not create the vendor.'}
          </span>
        ) : null}
      </div>
    </form>
  );
}

function TrackedInvites({ invites }: { invites: (TrackedInvite & { tenant_id?: string })[] }) {
  const queryClient = useQueryClient();
  const revoke = useMutation({
    mutationFn: (invite: TrackedInvite & { tenant_id?: string }) =>
      api(`/vendors/${invite.tenant_id}/invites/${invite.id}/revoke`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'vendors'] })
  });
  return (
    <div className="grid content-start gap-3">
      <div className="grid gap-1">
        <h3 className="text-[15px] font-medium text-foreground">Tracked invitations</h3>
        <span className="font-mono text-[10px] tracking-[0.12em] text-dim uppercase">Email-bound · single-use</span>
      </div>
      <ul className="grid">
        {invites.map(invite => (
          <li key={invite.id} className="grid grid-cols-[1fr_100px_60px_60px] items-center gap-3 py-2">
            <span className="truncate font-mono text-[12px] text-muted">{invite.email}</span>
            <Badge tone={INVITE_TONE[invite.state]}>{invite.state}</Badge>
            <span className="font-mono text-[11px] text-muted">{formatDay(invite.sent_at)}</span>
            {invite.state === 'registered' ? (
              <span className="text-right text-dim">—</span>
            ) : (
              <button
                type="button"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(invite)}
                className="cursor-pointer text-right text-[12px] text-primary hover:underline disabled:opacity-50"
              >
                Revoke
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
